import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, User, HardHat, Loader2, UserCog, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PhoneInput } from "@/components/PhoneInput";

const initial = { visitor_type: "visitor", full_name: "", company: "", phone: "", email: "", host_name: "", purpose: "", vehicle_plate: "" };

// One question per screen so the form always fits the kiosk viewport and the on-screen keyboard never hides the field.
const STEPS = [
  { key: "type", title: "Who's checking in?" },
  { key: "full_name", title: "What's your full name?", placeholder: "Jane Smith", required: true, autoComplete: "name" },
  { key: "phone", title: "Your phone number", placeholder: "(555) 123-4567", required: true, hint: "Used only to reach you while you're on site." },
  { key: "company", title: "Company & email", optional: true },
  { key: "host_name", title: "Who are you visiting?", placeholder: "Mr. Johnson", required: true, hint: "Host or person being visited" },
  { key: "purpose", title: "Purpose of your visit", placeholder: "Parent meeting / Delivery / HVAC maintenance", required: true },
  { key: "vehicle_plate", title: "Vehicle / license plate", placeholder: "ABC-1234", required: true, contractorOnly: true, autoCapitalize: "characters" },
  { key: "review", title: "Everything look right?" },
];

const DRAFT_KEY = "kiosk_checkin_draft";
const loadDraft = () => { try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; } };

export default function CheckInPage() {
  const draft = useMemo(loadDraft, []);
  const [form, setForm] = useState(draft?.form || initial);
  const [i, setI] = useState(draft?.i || 0);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const inputRef = useRef(null);
  const isContractor = form.visitor_type === "contractor";
  const steps = useMemo(() => STEPS.filter((s) => !s.contractorOnly || isContractor), [isContractor]);
  const step = steps[i];
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { const t = setTimeout(() => inputRef.current?.focus(), 80); return () => clearTimeout(t); }, [i]);
  // Survive an unexpected remount/reload (tablet keyboards, geofence re-check) without losing what was typed.
  useEffect(() => { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ form, i })); }, [form, i]);
  const clearDraft = () => sessionStorage.removeItem(DRAFT_KEY);

  const valid = () => {
    if (step.key === "type" || step.key === "review" || step.optional) return true;
    if (step.key === "phone") return form.phone.replace(/\D/g, "").length >= 10;
    return !!form[step.key]?.trim();
  };
  const next = () => { if (!valid()) { toast.error("This field is required"); return; } setI((n) => Math.min(n + 1, steps.length - 1)); };
  const back = () => setI((n) => Math.max(n - 1, 0));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
      if (!payload.email) delete payload.email;
      else payload.email = payload.email.toLowerCase();
      if (user?.id) payload.officer_id = user.id;
      const { data } = await api.post("/visits/checkin", payload);
      clearDraft();
      nav("/kiosk/success", { state: { mode: "in", name: data.full_name, host: data.host_name, time: data.checked_in_at, type: data.visitor_type, visit: data } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not check in.");
    } finally { setSubmitting(false); }
  };

  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); step.key === "review" ? submit() : next(); } };
  const isLast = step.key === "review";

  return (
    <div className="h-[100dvh] bg-slate-50 flex flex-col overflow-hidden safe-area" data-testid="checkin-page">
      <header className="shrink-0 px-6 pt-5 pb-3 flex items-center justify-between gap-4">
        <Link to="/kiosk" onClick={clearDraft} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-to-kiosk">
          <X className="h-5 w-5" /> <span className="text-sm uppercase tracking-widest">Cancel</span>
        </Link>
        <div className="flex items-center gap-1.5" data-testid="checkin-progress" aria-label={`Step ${i + 1} of ${steps.length}`}>
          {steps.map((s, idx) => <span key={s.key} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-primary" : idx < i ? "w-4 bg-primary/50" : "w-4 bg-slate-200"}`} />)}
        </div>
        {user ? (
          <div className="hidden sm:inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs" data-testid="checkin-on-duty">
            <UserCog className="h-3.5 w-3.5 text-emerald-700" /><span className="text-emerald-800">Handled by <b>{user.name}</b></span>
          </div>
        ) : <span className="w-24" />}
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto px-6">
        <div className="mx-auto max-w-2xl pt-2 pb-6" data-testid="checkin-form">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-2">{isContractor ? "Contractor" : "Visitor"} check-in · {i + 1}/{steps.length}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">{step.title}</h1>

          {step.key === "type" && (
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "visitor", label: "Visitor", Icon: User, sub: "Guests, parents, deliveries" }, { v: "contractor", label: "Contractor", Icon: HardHat, sub: "Vendors & service crews" }].map(({ v, label, Icon, sub }) => (
                <button key={v} type="button" onClick={() => { set("visitor_type")(v); setI(1); }} data-testid={`type-${v}-btn`}
                  className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 py-8 transition active:scale-[0.98] ${form.visitor_type === v ? "border-primary bg-primary text-white shadow-sm" : "border-slate-200 bg-white text-slate-800 hover:border-slate-300"}`}>
                  <Icon className="h-9 w-9" />
                  <span className="font-display text-2xl font-semibold tracking-tight">{label}</span>
                  <span className={`text-xs ${form.visitor_type === v ? "text-white/80" : "text-slate-500"}`}>{sub}</span>
                </button>
              ))}
            </div>
          )}

          {step.key === "phone" && (
            <PhoneInput ref={inputRef} required value={form.phone} onChange={set("phone")} onKeyDown={onKey} data-testid="input-phone" className={inputCls} placeholder={step.placeholder} />
          )}

          {step.key === "company" && (
            <div className="space-y-4">
              <Field label="Company (optional)"><input ref={inputRef} value={form.company} onChange={(e) => set("company")(e.target.value)} onKeyDown={onKey} data-testid="input-company" className={inputCls} placeholder="Acme Corp" autoComplete="organization" /></Field>
              <Field label="Email (optional)"><input type="email" inputMode="email" autoCapitalize="none" value={form.email} onChange={(e) => set("email")(e.target.value)} onKeyDown={onKey} data-testid="input-email" className={inputCls} placeholder="jane@acme.com" /></Field>
            </div>
          )}

          {["full_name", "host_name", "purpose", "vehicle_plate"].includes(step.key) && (
            <input ref={inputRef} required value={form[step.key]} onChange={(e) => set(step.key)(step.key === "vehicle_plate" ? e.target.value.toUpperCase() : e.target.value)} onKeyDown={onKey} enterKeyHint="next"
              autoComplete={step.autoComplete || "off"} autoCapitalize={step.autoCapitalize || "words"} data-testid={INPUT_IDS[step.key]} className={inputCls} placeholder={step.placeholder} />
          )}

          {step.key === "review" && (
            <dl className="rounded-2xl border-2 border-slate-200 bg-white divide-y divide-slate-100" data-testid="checkin-review">
              {[["Type", isContractor ? "Contractor" : "Visitor", 0], ["Name", form.full_name, 1], ["Phone", form.phone, 2], ["Company", form.company || "—", 3], ["Email", form.email || "—", 3],
                ["Visiting", form.host_name, 4], ["Purpose", form.purpose, 5], ...(isContractor ? [["Vehicle", form.vehicle_plate, 6]] : [])].map(([k, v, idx]) => (
                <button key={k} type="button" onClick={() => setI(idx)} className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left hover:bg-slate-50">
                  <dt className="text-xs uppercase tracking-widest text-slate-500 w-24 shrink-0">{k}</dt>
                  <dd className="flex-1 text-base font-medium text-slate-900 truncate">{v}</dd>
                  <span className="text-xs text-primary">Edit</span>
                </button>
              ))}
            </dl>
          )}

          {step.hint && <p className="mt-3 text-sm text-slate-500">{step.hint}</p>}
        </div>
      </main>

      {step.key !== "type" && (
        <footer className="shrink-0 border-t border-slate-200 bg-white px-6 py-3">
          <div className="mx-auto max-w-2xl flex items-center gap-3">
            <button type="button" onClick={back} data-testid="checkin-back-btn" className="inline-flex h-14 items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 text-slate-700 font-display text-lg font-semibold active:scale-[0.98]">
              <ArrowLeft className="h-5 w-5" /> Back
            </button>
            {isLast ? (
              <button type="button" onClick={submit} disabled={submitting} data-testid="submit-checkin-btn" className="flex-1 inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-primary text-white font-display text-xl font-semibold active:scale-[0.98] disabled:opacity-60">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} {submitting ? "Signing in…" : "Complete Check-In"}
              </button>
            ) : (
              <button type="button" onClick={next} data-testid="checkin-next-btn" className="flex-1 inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-primary text-white font-display text-xl font-semibold active:scale-[0.98]">
                {step.optional && !form.company && !form.email ? "Skip" : "Next"} <ArrowRight className="h-5 w-5" />
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

const INPUT_IDS = { full_name: "input-full-name", host_name: "input-host", purpose: "input-purpose", vehicle_plate: "input-plate" };
const inputCls = "w-full h-16 rounded-xl border-2 border-slate-200 bg-white px-5 text-xl font-body text-slate-900 placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-2 text-sm font-medium uppercase tracking-widest text-slate-500">{label}</span>
      {children}
    </label>
  );
}
